// @ts-nocheck
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand"; // Starter Custom Code
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
// import Typography, { TypographyProps } from "@insite/mobius/Typography"; // Foundation
import * as React from "react";
import { css } from "styled-components";

// Starter Custom Code - >
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState"; // Foundation
// <--- Starter Custom Code

/* Foundation - Start */
import ProductBrand, { ProductBrandStyles } from "@store/Overrides/Components/ProductBrand";
import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

/* Foundation - End */

type Props = WidgetProps & HasProduct;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
}

/* Foundation - Start */
const brandNameText: TypographyProps = {
    css: css`
        width: 100%;
        ${wrapInContainerStyles}
        font-size: ${({ theme }) => theme.design.typography.size.smaller};

        ${({ theme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        margin-bottom: 0.5em;
                    `,
                ],
                mediaOptions.max,
            )}
    `,
};
/* Foundation - End */

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        // Store Custom Code
        gap: 8,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: ${pxToRem(150)};
                    max-height: ${pxToRem(150)};
                }
            `,
        },
        nameText: brandNameText,
        nameLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: brandNameText,
        },
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h2",
        forwardAs: "h1",
        color: "primary",
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.medium};
            /* Bunzl Custom Code */
            margin-bottom: 0;
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product }) => {
    /* Foundation - Start */
    const parentProductState = useParentProductState();

    const titleTextStyles: TypographyPresentationProps = {
        ...styles.titleText,
        forwardAs: parentProductState?.productTitle !== product.productTitle ? "h2" : "h1",
        css: css`
            ${styles.titleText?.css}
            margin-top: 0;
            margin-bottom: 0;
        `,
    };
    /* Foundation - End */

    return (
        <>
            <GridContainer {...styles.container} data-test-selector="productDetails_pageTitle_container">
                {product.brand && (
                    <GridItem {...styles.brandGridItem} data-test-selector="productDetails_pageTitle_brandGridItem">
                        <ProductBrand
                            brand={product.brand}
                            showLogo={true}
                            extendedStyles={styles.brandStyles}
                            data-test-selector="productDetails_pageTitle_brand" /* Store Custom Code */
                        />
                    </GridItem>
                )}
                <GridItem
                    {...styles.titleGridItem}
                    data-test-selector="productDetails_pageTitle_titleGridItem" /* Store Custom Code */
                >
                    {/* Starter Custom Code - > */}
                    {parentProductState?.productTitle !== product.productTitle && (
                        <VisuallyHidden>
                            <Typography
                                forwardAs="h1"
                                data-test-selector="productDetails_pageTitle_parentTitleHidden" /* Store Custom Code */
                            >
                                {parentProductState?.productTitle}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    {/* <--- Starter Custom Code */}
                    <Typography
                        {
                            ...titleTextStyles /* Starter Custom Code */
                        }
                        data-test-selector="productDetails_pageTitle_mainTitle" /* Store Custom Code */
                    >
                        {product.productTitle}
                    </Typography>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPageTitle),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
